import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "exit_intent_dismissed_v1";
const COOLDOWN_DAYS = 14;
const SESSION_KEY = "exit_intent_shown_session";
const COUPON_CODE = "BIENVENUE10";

const isMobile = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 768px)").matches;

function shouldSkipShowing() {
  if (typeof window === "undefined") return true;
  // Skip on checkout/auth pages
  const path = window.location.pathname;
  if (
    path.startsWith("/checkout") ||
    path.startsWith("/login") ||
    path.startsWith("/register") ||
    path.startsWith("/admin")
  )
    return true;
  // Already shown this tab session
  if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
  // Dismissed within cooldown
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (data?.at) {
        const ageDays = (Date.now() - data.at) / (1000 * 60 * 60 * 24);
        if (ageDays < COOLDOWN_DAYS) return true;
      }
    }
  } catch {
    /* ignore */
  }
  return false;
}

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();

  // Hide instantly on route change for excluded paths
  useEffect(() => {
    if (shouldSkipShowing()) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (shouldSkipShowing()) return;

    const trigger = () => {
      if (shouldSkipShowing()) return;
      sessionStorage.setItem(SESSION_KEY, "1");
      setOpen(true);
    };

    // Desktop: mouseout to top of viewport
    const onMouseOut = (e) => {
      if (e.relatedTarget || e.toElement) return;
      if (e.clientY > 10) return;
      trigger();
    };

    // Mobile: fire after 30s of inactivity (no scroll/touch) or on tab hidden
    let mobileTimer = null;
    const armMobileTimer = () => {
      if (mobileTimer) clearTimeout(mobileTimer);
      mobileTimer = setTimeout(() => trigger(), 30000);
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") trigger();
    };
    const onScroll = () => armMobileTimer();
    const onTouch = () => armMobileTimer();

    if (isMobile()) {
      armMobileTimer();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("touchstart", onTouch, { passive: true });
      document.addEventListener("visibilitychange", onVisibility);
    } else {
      document.addEventListener("mouseout", onMouseOut);
    }

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("touchstart", onTouch);
      document.removeEventListener("visibilitychange", onVisibility);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const dismiss = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ at: Date.now() })
      );
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      toast.success(t("exit_intent.copied", "Code copié !"));
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copie impossible — code : " + COUPON_CODE);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 sm:px-6"
      data-testid="exit-intent-popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        onClick={dismiss}
        className="absolute inset-0 bg-[#000B1A]/85 backdrop-blur-sm"
        aria-label={t("exit_intent.close", "Fermer")}
        data-testid="exit-intent-backdrop"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md bg-[#001F3F] border border-[#FFD700]/40 shadow-[0_20px_80px_-20px_rgba(255,215,0,0.35)] animate-in fade-in zoom-in-95 duration-200"
        style={{
          backgroundImage:
            "radial-gradient(circle at top left, rgba(255,215,0,0.10), transparent 55%)",
        }}
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 p-2 text-white/60 hover:text-white"
          aria-label={t("exit_intent.close", "Fermer")}
          data-testid="exit-intent-close"
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div className="p-8 sm:p-10 text-white">
          <div className="inline-flex items-center gap-1.5 bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-[10px] font-bold uppercase tracking-[0.3em] px-2.5 py-1">
            <Sparkles size={12} strokeWidth={2} /> Offre exclusive
          </div>

          <h2
            id="exit-intent-title"
            className="font-display mt-5 text-3xl sm:text-4xl font-bold uppercase tracking-tighter leading-[0.95]"
          >
            Attendez !<br />
            <span className="text-[#FFD700]">–10 % offerts</span><br />
            sur votre commande.
          </h2>

          <p className="mt-4 text-sm leading-relaxed text-white/70">
            On vous offre 10 % de remise pour fêter votre arrivée chez Nord Stella Tech.
            Pas de spam, juste un code à utiliser quand vous voulez.
          </p>

          <div className="mt-6 border-2 border-dashed border-[#FFD700]/50 bg-[#FFD700]/5 p-5 flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FFD700]">
                Votre code
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold tracking-[0.18em] text-white mt-1 select-all">
                {COUPON_CODE}
              </div>
            </div>
            <button
              type="button"
              onClick={copyCode}
              className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.2em] text-[10px] font-bold px-4 py-3"
              data-testid="exit-intent-copy"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? "Copié" : "Copier"}
            </button>
          </div>

          <a
            href="/shop"
            onClick={(e) => {
              // Persist dismissal so the popup doesn't reopen on next page
              try {
                localStorage.setItem(
                  STORAGE_KEY,
                  JSON.stringify({ at: Date.now() })
                );
              } catch {
                /* ignore */
              }
              setOpen(false);
              // Let the browser handle the navigation
              e.stopPropagation();
            }}
            className="mt-6 w-full text-center bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold py-4 block"
            data-testid="exit-intent-cta"
          >
            Voir la boutique →
          </a>

          <button
            type="button"
            onClick={dismiss}
            className="mt-3 w-full text-center text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 hover:text-white/70 py-2"
            data-testid="exit-intent-dismiss"
          >
            Non merci, je quitte
          </button>
        </div>
      </div>
    </div>
  );
}
